// src/components/SortableSidebar.tsx
// DnD-обёртка для пунктов сайдбара
import React, { useState, useRef } from 'react';
import { GripVertical } from 'lucide-react';

interface SortableItemProps {
  index: number;
  total: number;
  onReorder: (from: number, to: number) => void;
  isEditMode: boolean;
  children: React.ReactNode;
}

// ─── Обёртка для одного пункта ───
export const SortableItem: React.FC<SortableItemProps> = ({ index, total, onReorder, isEditMode, children }) => {
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.DragEvent) => {
    if (!isEditMode) { e.preventDefault(); return; }
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragEnd = () => {
    setDragging(false);
    setOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOver(true);
  };

  const handleDragLeave = () => setOver(false);

  const handleDrop = (e: React.DragEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    setOver(false);
    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!isNaN(from) && from !== index) {
      onReorder(from, index);
    }
  };

  return (
    <div
      ref={ref}
      draggable={isEditMode}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: 'relative',
        opacity: dragging ? 0.4 : 1,
        transition: 'opacity 0.15s, transform 0.15s',
        transform: over && !dragging ? 'scale(1.02)' : 'scale(1)',
        // WHOLE_DRAG_FIX: весь пункт можно тянуть
        cursor: isEditMode ? 'grab' : 'inherit',
        userSelect: isEditMode ? 'none' : 'auto',
        WebkitUserSelect: isEditMode ? 'none' : 'auto',
      }}
    >
      {/* Хват для перетаскивания */}
      {isEditMode && (
        <div
          style={{
            position: 'absolute',
            left: 2, top: '50%', transform: 'translateY(-50%)',
            color: over ? '#10b981' : '#94a3b8',
            display: 'flex', alignItems: 'center',
            zIndex: 2, pointerEvents: 'none',
          }}
        >
          <GripVertical size={14} />
        </div>
      )}

      {/* Индикатор места вставки */}
      {over && !dragging && (
        <div style={{
          position: 'absolute',
          left: 0, right: 0, top: 0,
          height: 3,
          background: 'linear-gradient(90deg, #10b981, #059669)',
          borderRadius: 2,
          boxShadow: '0 0 8px rgba(16,185,129,0.6)',
          animation: 'pulseDrop 0.8s ease-in-out infinite',
          zIndex: 3,
        }} />
      )}

      {/* WHOLE_DRAG_FIX: обёртка — в edit-режиме NavLink не перехватывает drag */}
      <div style={{
        pointerEvents: isEditMode ? 'none' : 'auto',
        WebkitUserDrag: isEditMode ? 'none' : 'auto',
      } as React.CSSProperties}>
        {children}
      </div>

      {/* CSS анимации */}
      <style>{`
        @keyframes pulseDrop {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

// ─── Кнопка редактирования порядка ───
export const EditOrderButton: React.FC<{ isEditMode: boolean; onToggle: () => void; onReset: () => void; saving: boolean }> =
  ({ isEditMode, onToggle, onReset, saving }) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {isEditMode && (
        <button
          onClick={onReset}
          style={{
            padding: '4px 8px', border: 'none', borderRadius: 6,
            background: 'rgba(239,68,68,0.1)', color: '#ef4444',
            cursor: 'pointer', fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
          }}
          title="Вернуть исходный порядок"
        >
          ↺ Сброс
        </button>
      )}
      <button
        onClick={onToggle}
        style={{
          padding: '4px 10px', border: 'none', borderRadius: 6,
          background: isEditMode ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(139,92,246,0.1)',
          color: isEditMode ? '#fff' : '#8b5cf6',
          cursor: 'pointer', fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
        title={isEditMode ? 'Сохранить порядок' : 'Редактировать порядок (перетаскивание)'}
      >
        {saving ? '⏳' : isEditMode ? '✓ Готово' : '✎ Порядок'}
      </button>
    </div>
  );

export default { SortableItem, EditOrderButton };
